/**
 * User Context Cookie Utility
 * Stores getUserContext API response in a cookie with parent domain scope
 * so it's shared across subdomains (e.g., platform.vidhyantra.com, org.vidhyantra.com).
 * Used for accessible domains in navbar/context switcher; fallback to getUserContext API if missing.
 */

import Cookies from 'js-cookie'
import type { UserContextResponse } from '@/services/api/shared/navigation.service'
import { logger } from '@/lib/logger'
import { getParentCookieDomain } from '@/utils/cookie-domain.util'

const USER_CONTEXT_COOKIE_NAME = 'userContext'
const COOKIE_MAX_AGE_DAYS = 7
const COOKIE_PATH = '/'

/**
 * Set user context response in cookie (parent domain scope)
 * Called after getUserContext API succeeds (e.g. post-login)
 */
export function setUserContextCookie(data: UserContextResponse): void {
  try {
    const domain = getParentCookieDomain()
    const value = JSON.stringify(data)
    // Cookie size limit ~4KB; if too large, storing just accessibleDomains
    if (value.length > 3800) {
      const compact = {
        user: data.user,
        domainNavigation: { accessibleDomains: data.domainNavigation?.accessibleDomains ?? [] },
      }
      const compactStr = JSON.stringify(compact)
      if (compactStr.length > 3800) {
        logger.warn('[userContext] Response too large for cookie; storing accessibleDomains only')
        const minimal = { domainNavigation: { accessibleDomains: data.domainNavigation?.accessibleDomains ?? [] } }
        Cookies.set(USER_CONTEXT_COOKIE_NAME, JSON.stringify(minimal), {
          path: COOKIE_PATH,
          expires: COOKIE_MAX_AGE_DAYS,
          sameSite: 'lax',
          secure: window.location.protocol === 'https:',
          ...(domain && { domain }),
        })
        return
      }
      Cookies.set(USER_CONTEXT_COOKIE_NAME, compactStr, {
        path: COOKIE_PATH,
        expires: COOKIE_MAX_AGE_DAYS,
        sameSite: 'lax',
        secure: window.location.protocol === 'https:',
        ...(domain && { domain }),
      })
      return
    }
    Cookies.set(USER_CONTEXT_COOKIE_NAME, value, {
      path: COOKIE_PATH,
      expires: COOKIE_MAX_AGE_DAYS,
      sameSite: 'lax',
      secure: window.location.protocol === 'https:',
      ...(domain && { domain }),
    })
  } catch (e) {
    logger.warn('[userContext] Failed to set cookie:', e)
  }
}

/**
 * Get user context from cookie (e.g. for navbar accessible domains)
 * Returns null if missing or invalid
 */
export function getUserContextFromCookie(): UserContextResponse | null {
  try {
    const raw = Cookies.get(USER_CONTEXT_COOKIE_NAME)
    if (!raw) return null
    const parsed = JSON.parse(raw) as UserContextResponse
    if (!parsed?.domainNavigation?.accessibleDomains) return null
    return parsed
  } catch {
    return null
  }
}

/**
 * Remove user context cookie (e.g. on logout)
 */
export function clearUserContextCookie(): void {
  try {
    const domain = getParentCookieDomain()
    Cookies.remove(USER_CONTEXT_COOKIE_NAME, { path: COOKIE_PATH, ...(domain && { domain }) })
  } catch {
    // ignore
  }
}
